import { Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";
import _ from "lodash";
import type { FunctionComponent } from "react";
import { connect } from "react-redux";
import type { ISegmentsData } from "app/services/conversion/ConversionSegmentsService";
import styled from "styled-components";
import { toggleDeleteSegmentModal, toggleRenameSegmentModal } from "../../actions/conversionModuleActions";
import type { IConversionModuleState } from "../../reducers/_reducers/ConversionModuleReducer";
import type { IConversionNavAdditionalOptionsProps } from "./ConversionNavAdditionalOptionsEllipsis";
import { ConversionNavAdditionalOptions } from "./ConversionNavAdditionalOptionsEllipsis";
import DeleteSegmentGroupConfirmation from "./modals/DeleteSegmentGroupConfirmation";
import RenameSegmentGroupConfirmation from "./modals/RenameSegmentGroupModal";
import { ConversionSegmentTitle } from "./StyledComponents";
import { SwTrack } from "app/services/SwTrack";
import { useCurrentStateAndParams } from "@uirouter/react";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const SegmentContainer: any = styled.div`
    display: flex;
    align-items: center;
    text-transform: capitalize;
    flex-shrink: 0;
`;
SegmentContainer.displayName = "SegmentContainer";

interface IConversionSegmentsQueryBarProps {
    deleteGroupSegmentModalIsOpen?: boolean;
    renameGroupSegmentModalIsOpen?: boolean;
    gid?: string;
    toggleDeleteSegmentModal: (isOpen, gid) => {};
    toggleRenameSegmentModal: (isOpen, gid) => {};
    segmentsLoading: boolean;
    segments: ISegmentsData;
}

const ConversionSegmentsQueryBar: FunctionComponent<IConversionSegmentsQueryBarProps> = (props) => {
    const services = {
        swNavigator: proNavigator,
    };
    const { params } = useCurrentStateAndParams();
    const { gid, sid, duration } = params;
    const { segmentGroups, segments } = props.segments;
    const settingsProps: IConversionNavAdditionalOptionsProps = {
        useSettingsBtn: true,
        onClickDelete: (gidParam) => {
            SwTrack.all.trackEvent("Button", "click", `QueryBar/Delete group/${gidParam}`);
            props.toggleDeleteSegmentModal(true, gidParam);
        },
        onClickRename: (gidParam) => {
            SwTrack.all.trackEvent("Button", "click", `QueryBar/Rename group/${gidParam}`);
            props.toggleRenameSegmentModal(true, gidParam);
        },
        onClickEdit: (action: string, gidParam, country) => {
            SwTrack.all.trackEvent("Button", "click", `QueryBar/${action} sites/${gidParam}`);
            services.swNavigator.go("conversion-wizard", { gid: gidParam, country, duration });
        },
        onToggleEllipsis: () => {
            SwTrack.all.trackEvent("Button", "click", `Side bar/Additional Options/Toggle`);
        },
        dropDownStyle: {
            marginLeft: 8,
        },
    };

    const render = () => {
        const segmentName = sid ? _.get(segments, [sid, "segmentName"], "") : "";
        return (
            <Flex height="64px">
                <SegmentContainer>
                    <Tooltip
                        content={segmentName}
                        enabled={!!segmentName}
                    >
                        <Text
                            fontVariant="subtitle1"
                            data-automation="conversion-group-name"
                        >
                            {sid ? (
                                <ConversionSegmentTitle>
                                    {_.get(segments, [sid, "domain"], "")} {segmentName ? ` - ${segmentName}` : ""}
                                </ConversionSegmentTitle>
                            ) : (
                                // seems like kind of problem with typing of _.get
                                // it combines ISegmentGroupData with string type
                                _.get(segmentGroups, [gid, "name"], "").replace(/_/g, " ")
                            )}
                        </Text>
                    </Tooltip>
                    {!sid ? <ConversionNavAdditionalOptions {...settingsProps} /> : null}
                </SegmentContainer>
                <DeleteSegmentGroupConfirmation
                    isOpen={props.deleteGroupSegmentModalIsOpen}
                    gid={props.gid}
                    segmentsData={props.segments}
                />
                <RenameSegmentGroupConfirmation
                    isOpen={props.renameGroupSegmentModalIsOpen}
                    gid={props.gid}
                    segmentsData={props.segments}
                />
            </Flex>
        );
    };

    if (props.segmentsLoading) {
        return null;
    }

    return render();
};

function mapStateToProps(store) {
    const {
        segmentsModule,
        conversionModule,
    }: { segmentsModule: ISegmentsModule; conversionModule: IConversionModuleState } = store;
    return {
        ...conversionModule,
        segmentsLoading: segmentsModule.segmentsLoading,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        toggleDeleteSegmentModal: (isOpen, gid) => {
            dispatch(toggleDeleteSegmentModal(isOpen, gid));
        },
        toggleRenameSegmentModal: (isOpen, gid) => {
            dispatch(toggleRenameSegmentModal(isOpen, gid));
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ConversionSegmentsQueryBar);
